import { useState } from 'react';

import { parse, stringify } from 'yaml';

import { ModalPortal } from '../../components/modal-portal/ModalPortal';

import styles from './TaskForm.module.scss';
import { sendEvent, useEventNotification } from '../visual-analytics/hooks/events';
import { FancyButton } from '../../components/primitives/FancyButton';
import { TpPromptTaskContext, TpTaskFormData } from './elements/interfaces';
import TemplateEditor from '../../components/yaml-editor/TemplateEditor';
import { useApiContext } from '../../api';
import { evalBooleanExpression } from '../../components/safeExpressions';
import schemaTaskFormTemplate from '../../json-schemas/schemaTaskFormTemplate';
import { taskFormElemFactory } from './elements/factory';
import { TpAssetInfo } from '../../data/assets/interfaces';
import { useAppDispatch } from '../../app/hooks';
import { showErrorMessage } from '../../components/message-boxes/ErrorMessage';
import { TpConfirmData, promptConfirmationBox } from '../../components/message-boxes/ConfirmBox';
import { useNavigate } from 'react-router-dom';
import { PollingConfiguration, addPollingConfiguration } from '../../data/pollingSlice';
import { taskExecutionPollingId } from '../../data/pollingService';
import { addNewTaskExecution } from '../../data/taskExecutionSlice';

const MSGTaskForm = 'PromptTaskForm';

// Call this function to show a UI form prompting for a task
export function promptTask(promptTaskContext: TpPromptTaskContext) {
  sendEvent(MSGTaskForm, promptTaskContext);
}

// Executes all data validations defined in a task form, and returns a list of errors (empty list = all have passed)
function performValidations(activeFormContext: TpPromptTaskContext, template: any, parsedFormData: any): string[] {
  if (!template.formDefinition.validations) return [];
  const errorList: string[] = [];
  for (const validationDef of template.formDefinition.validations) {
    if (!evalBooleanExpression({ expressionString: validationDef.test, parameters: parsedFormData })) {
      errorList.push(validationDef.errorMessage);
    }
  }
  return errorList;
}

// React component rendering the task form in a modal portal.
// Initially this component renders nothing, but a form can be activated by calling promptTask
export function TaskForm() {
  const api = useApiContext();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const [activeFormContext, setActiveFormContext] = useState<TpPromptTaskContext | null>(null);
  const [templateString, setTemplateString] = useState<string | null>(null);
  const [formData, setFormData] = useState<TpTaskFormData>({ elemValues: {} });
  const [error, setError] = useState<string | null>(null);
  const [dataErrorList, setDataErrorList] = useState<string[]>([]);
  const [assetInfo, setAssetInfo] = useState<TpAssetInfo | null>(null);

  let template: any;
  if (templateString) template = parse(templateString);

  function initialiseData(promptTaskContext: TpPromptTaskContext, templateString: string) {
    try {
      const template = parse(templateString);
      const theFormData: TpTaskFormData = { elemValues: {} };
      for (const elemDef of template.formDefinition.elements)
        if (taskFormElemFactory(elemDef.type).hasData)
          taskFormElemFactory(elemDef.type).setInitialData!(elemDef, promptTaskContext, theFormData);
      setFormData(theFormData);
      setError(null);
      setDataErrorList([]);
    } catch (error) {
      setError(String(error));
    }
  }

  // Listen to notifications indicating that we should show a task form
  useEventNotification(
    MSGTaskForm,
    (tpe: string, contextInput: any) => {
      // Initialise the form
      const promptTaskContext = contextInput as TpPromptTaskContext;
      setActiveFormContext(promptTaskContext);
      setTemplateString(stringify(promptTaskContext.taskDefinitionTemplate));
      setAssetInfo(promptTaskContext.assetInfo);
      initialiseData(promptTaskContext, stringify(promptTaskContext.taskDefinitionTemplate));
    },
    [],
  );

  // This function is called by individual element types that contain data (parameter values) to update their content
  function updateElemValue(elemId: string, newValue: any) {
    setFormData({
      ...formData,
      elemValues: {
        ...formData.elemValues,
        [elemId]: newValue,
      },
    });
  }

  if (!template || !activeFormContext) return null; //  no active form - we render nothing

  // Parse the current content of the form elements
  const parsingErrorList: string[] = [];
  const parsedFormData: any = {};
  for (const elem of template.formDefinition.elements) {
    if (taskFormElemFactory(elem.type).hasData) {
      const parsingInfo = taskFormElemFactory(elem.type).parseElemValue!(
        activeFormContext!,
        elem,
        formData.elemValues[elem.id],
      );
      parsedFormData[elem.id] = parsingInfo.value;
      if (parsingInfo.errorString) parsingErrorList.push(`${elem.displayName}: ${parsingInfo.errorString}`);
    }
  }

  // Render all elements
  let elements = null;
  if (!error) {
    try {
      if (template) {
        elements = template.formDefinition.elements.map((elem: any) => {
          if (elem.visibleCondition) {
            if (!evalBooleanExpression({ expressionString: elem.visibleCondition, parameters: parsedFormData }))
              return false;
          }
          return (
            <div key={elem.id}>
              {taskFormElemFactory(elem.type).generate(elem, activeFormContext!, formData, updateElemValue)}
            </div>
          );
        });
      }
    } catch (error) {
      setError(String(error));
    }
  }

  function handleCloseTemplateEditor(newTemplate: string) {
    setError(null);
    setTemplateString(newTemplate);
    initialiseData(activeFormContext!, newTemplate);
  }

  function performStart() {
    const confirmData: TpConfirmData = {
      title: `Your task execution has just started`,
      description: `Would you like to proceed to the 'Task Executions' page? \n\n`,
      cancelButtonLabel: `Close`,
    };

    if (parsingErrorList.length > 0) {
      setDataErrorList(parsingErrorList);
    } else {
      const validationErrorList = performValidations(activeFormContext!, template!, parsedFormData);
      if (validationErrorList.length > 0) {
        setDataErrorList(validationErrorList);
      } else {
        setActiveFormContext(null);
        setTemplateString(null);
        api.task
          .createTask(assetInfo!.id, activeFormContext!.taskDefinitionTemplate.taskDefinitionId!, parsedFormData)
          .then((taskExecution) => {
            if (taskExecution) {
              const pollingConfig: PollingConfiguration = {
                identifier: taskExecutionPollingId,
                interval: 20000,
              };
              dispatch(addNewTaskExecution({ taskExecution }));
              dispatch(addPollingConfiguration(pollingConfig));
            }

            return promptConfirmationBox(confirmData).then((confirmed) => {
              if (confirmed) {
                navigate(`/task-executions`);
              }
            });
          })
          .catch((error) => {
            showErrorMessage(`Error when starting task execution: ${error}`);
          });
      }
    }
  }
  return (
    <ModalPortal
      close={() => {
        setActiveFormContext(null);
      }}
    >
      <div className={styles.taskFormWrapper}>
        <div className={styles.taskFormTitle}>{activeFormContext.taskDefinitionTemplate.displayName}</div>
        {error && <div className={styles.errorMessage}>{error}</div>}
        <div className={styles.taskFormBody}>{elements}</div>
        <div className={styles.taskFormFooter}>
          <div className={styles.taskFormFooterMessage}>
            {dataErrorList.map((errorString, idx) => (
              <div key={`k${idx}`} className={styles.errorMessage}>
                {errorString}
              </div>
            ))}
          </div>
          <div className={styles.taskFormFooterButtons}>
            <FancyButton
              iconName="fa-cog" // @todo: define the icon in the template
              onClick={performStart}
            >
              Start
            </FancyButton>
          </div>
        </div>
        <TemplateEditor
          initialTemplate={templateString!}
          contentModified={handleCloseTemplateEditor}
          yamlSchema={schemaTaskFormTemplate}
        />
      </div>
    </ModalPortal>
  );
}
